import api, { ApiResponse, PaginatedResponse } from './api';

export interface Reimbursement {
  id: number;
  employeeId: number;
  paidByEmployeeId: number;
  expenseFor: string;
  amountINR: number | null;
  amountEUR: number | null;
  totalCostINR: number;
  remarks: string | null;
  invoicePath: string | null;
  paymentScreenshotPath: string | null;
  status: string;
  approvedBy: number | null;
  approvedAt: string | null;
  rejectedReason: string | null;
  createdAt: string;
  updatedAt: string;
  employee: {
    id: number;
    firstName: string;
    lastName: string;
    employeeCode: string;
    email: string;
  };
  paidByEmployee: {
    id: number;
    firstName: string;
    lastName: string;
    employeeCode: string;
    email: string;
  };
}

export interface CreateReimbursementData {
  employeeId: number;
  paidByEmployeeId: number;
  expenseFor: string;
  amountINR?: number;
  amountEUR?: number;
  totalCostINR: number;
  remarks?: string;
  invoice?: File;
  paymentScreenshot?: File;
}

export interface UpdateReimbursementData {
  status?: string;
  rejectedReason?: string;
}

export const reimbursementService = {
  async create(data: CreateReimbursementData): Promise<Reimbursement> {
    const formData = new FormData();
    formData.append('employeeId', String(data.employeeId));
    formData.append('paidByEmployeeId', String(data.paidByEmployeeId));
    formData.append('expenseFor', data.expenseFor);
    if (data.amountINR !== undefined) {
      formData.append('amountINR', String(data.amountINR));
    }
    if (data.amountEUR !== undefined) {
      formData.append('amountEUR', String(data.amountEUR));
    }
    formData.append('totalCostINR', String(data.totalCostINR));
    if (data.remarks) {
      formData.append('remarks', data.remarks);
    }
    if (data.invoice) {
      formData.append('invoice', data.invoice);
    }
    if (data.paymentScreenshot) {
      formData.append('paymentScreenshot', data.paymentScreenshot);
    }

    const response = await api.post<ApiResponse<Reimbursement>>(
      '/reimbursements',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data!;
  },

  async getAll(filters?: {
    employeeId?: number;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Reimbursement>> {
    const params = new URLSearchParams();
    if (filters?.employeeId) params.append('employeeId', String(filters.employeeId));
    if (filters?.status) params.append('status', filters.status);
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));

    const response = await api.get<PaginatedResponse<Reimbursement>>(
      `/reimbursements?${params.toString()}`
    );
    return response.data;
  },

  async getById(id: number): Promise<Reimbursement> {
    const response = await api.get<ApiResponse<Reimbursement>>(`/reimbursements/${id}`);
    return response.data.data!;
  },

  async update(id: number, data: UpdateReimbursementData): Promise<Reimbursement> {
    const response = await api.patch<ApiResponse<Reimbursement>>(
      `/reimbursements/${id}`,
      data
    );
    return response.data.data!;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/reimbursements/${id}`);
  },
};
